/* eslint-disable max-len */
/* eslint-disable ban/ban */
import { cart } from "@/theme/Cart";
import { CartMain } from "../Cart/CartMain";
import { Component, Prop, Ref, Watch } from "@eastsideco/salvo-ts";
import debounce from 'lodash/debounce';
import { ProductOptionPicker } from "./ProductOptionPicker";
import { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import { setVariantUrlParam, getQuantityMax } from "@/utils/products";
import { formatMoney } from "@/utils/formatting";
import { SideCart } from '../SideCart/SideCart';
import { ThemeComponent } from "@/theme/ThemeComponent";
import { scrollToElement } from "@/utils/scroll";

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop optionStyle!: 'dropdown' | 'buttons';
    @Prop variantsQty!: string;
    @Prop strings!: string;
    @Prop preOrder!: string;
    @Prop hasStock!: string;
    @Prop isUkStore!: string;
    @Ref options!: ProductOptionPicker;
    @Ref({ required: false }) submitButton!: HTMLButtonElement[];
    @Ref({ required: false }) submitButtonV2!: HTMLButtonElement[];
    @Ref({ required: false }) buyNow!: HTMLButtonElement[];
    @Ref({ required: false }) stickyBar!: HTMLElement;
    @Ref error!: HTMLElement;
    @Ref price!: HTMLElement;
    @Ref({ required: false }) quantity!: QuantitySpinner | null;
    @Ref({ required: false }) mobileStickyButton!: HTMLElement;
    @Ref({ required: false }) tests!: HTMLElement[];

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant | null = null;
    protected quantityVal: number = 1;
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;
    private cartMain: CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart: SideCart | null = document.body.querySelector('side-cart');
    private footer = document.body.querySelector('.site-footer');

    init() {
        this.$subscribe(this.options.onSelectedVariantChange, (variant) => this.selectedVariant = variant);
        this.selectedVariant = this.options.selectedVariant;

        this.stickyToggle();

        // Quantity Funtions
        this.setQuantityMax();
        document.addEventListener('cartChanged', (e: Event) => {
            this.setQuantityMax();
        });

        this.checkQuantityChange();

        for (const button of [...(this.submitButton || []), ...(this.submitButtonV2 || [])]) {
            this.$listen(button, 'click', () => {
                this.addToCart('atc');
                scrollToElement(this);
            });
        }

        for (const button of this.buyNow) {
            this.$listen(button, 'click', () => {
                this.addToCart('buynow');
                scrollToElement(this);
            });
        }

        if (this.mobileStickyButton) {
            this.$listen(this.mobileStickyButton, 'click', () => {
                if (this.selectedVariant == null) {
                    document.dispatchEvent(new Event('openSizeDrawer'));
                } else {
                    this.addToCart('atc');
                }
            });
        }
    }

    private stickyToggle() {
        const stickyBar = this.stickyBar;

        if (!stickyBar) {
            return;
        }

        const targetElement = document.querySelector('.product-form__refbutton'); // Change the selector to your target element

        if (!targetElement) {
            return; // Exit early if the target element is not found
        }

        // Function to check and update the class
        const checkAndUpdateClass = () => {
            const targetElementRect = targetElement.getBoundingClientRect();
            const isTargetElementInView = targetElementRect.bottom > 0 && targetElementRect.top < window.innerHeight;

            let isFooterInView = false;
            if (this.footer) {
                const footerRect = this.footer.getBoundingClientRect();
                isFooterInView = footerRect.bottom > 0 && footerRect.top < window.innerHeight;
            }

            if (!isTargetElementInView) {
                if (!isFooterInView) {
                    stickyBar.classList.add('visible');
                } else {
                    stickyBar.classList.remove('visible');
                }
            } else {
                stickyBar.classList.remove('visible');
            }
        };

        // Check and update the class on page load
        window.addEventListener('load', checkAndUpdateClass);

        // Check and update the class while scrolling
        window.addEventListener('scroll', checkAndUpdateClass);
    }


    protected showError(msg: string | null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
        this.setQuantityMax();
        this.handlePrice();
    }

    @Watch('loading')
    protected handleLoading() {
        for (const button of [...(this.submitButton || []), ...(this.submitButtonV2 || [])]) {
            button.disabled = this.loading;
        }
    }

    /**
	 *  Update Price;
	 * @returns
	 */
    handlePrice() {
        if (this.selectedVariant === null) { return; }
        const updatedPrice = formatMoney(this.selectedVariant.price) as string;

        if (updatedPrice === null) { return; }
        this.price.innerHTML = updatedPrice;
    }

    /**
	 * Set maximum variant quantity on variant change.
	 */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);
        this.selectedCartQty = this.checkCartQty(this.selectedVariant);

        if (this.selectedCartQty > 0) {
            this.maxQty = String(~~this.maxQty - this.selectedCartQty);
        }

        this.handleQtyInput();
        if (~~this.maxQty === 0) {
            this.handleOutOfStock();
            this.quantity?.input.setAttribute('max', this.maxQty);
            this.quantity?.classList.add('visually-hidden');
            return;
        }

        this.quantity?.input.setAttribute('max', this.maxQty);
        this.quantity?.classList.remove('visually-hidden');
        this.handleInStock();
    }

    handleQtyInput() {
        const qtyInput = this.quantity?.input.value;

        if (qtyInput && (~~qtyInput > ~~this.maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = this.maxQty;
            return;
        }
    }

    /**
	 * Check if selected variant is in cart? get quanity.
	 * @param variant - selected variant.
	 * @returns
	 */
    protected checkCartQty(variant: Shopify.Liquid.Variant | null) {
        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
	 * If quantity switcher exists, update Quantity on value change.
	 */
    protected checkQuantityChange() {
        if (this.quantity === null) { return; }
        if (this.quantity) {
            this.$subscribe(this.quantity?.onChange, (value) => {
                this.quantityVal = value;
            });
        }
    }

    /**
	 * Handle Add to Cart.
	 */
    protected addToCart(type: string) {
        if (this.selectedVariant == null) {
            // document.dispatchEvent(new Event('openSizeDrawer'));
            this.options.classList.toggle('no-selection');

            if(type == 'buynow') {
                this.buyNow.forEach((button) => {
                    button.parentElement?.classList.toggle('no-option');
                    setTimeout(() => { button.parentElement?.classList.toggle('no-option'); }, 1000);
                });
            } else {
                (this.submitButton || []).forEach((button) => {
                    button.parentElement?.classList.toggle('no-option');
                    setTimeout(() => { button.parentElement?.classList.toggle('no-option'); }, 1000);
                });
            }

        } else {
            this.loading = true;
            const variant = this.selectedVariant;
            // Define line item properties shape
            const properties: { [key: string]: string } = {};

            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            if (this.preOrder == 'true') {
                if (properties) {
                    properties['Pre Order'] = 'Yes';
                }
            }

            // EasySize / Fit Finder order tracking (UK store only): attach the EasySize
            // pageview id as a line item property so purchase data is attributed to the session.
            if (this.isUkStore == 'true') {
                // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
                let _esid: string;
                try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle
                    _esid = (window as any).EasySizeParametersDebug.easysize.pageview_id.toString();
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                    _esid = '-1';
                }
                properties['_esid'] = _esid;
            }

            cart.addItem({
                id: variant.id,
                quantity: this.quantityVal,
                properties: properties
            }).then((item) => {
                document.dispatchEvent(new Event('cartChanged'));
                if(type == 'buynow') {
                    window.location.href = '/checkout';
                }
            }).catch((event: unknown | null) => {
                if (event instanceof Error) {
                    this.showError(event.message);
                }
            });

            this.loading = false;
        }
    }

    /**
	 * Diasable AdToCart if variant is out of stock.
	 */
    protected handleOutOfStock() {
        for (const button of (this.submitButton || [])) {
            if (this.selectedVariant == null) {
                if (this.hasStock == 'true') {
                    if (this.preOrder == 'true') {
                        button.innerHTML = this.getString('preOrder');
                    } else {
                        button.innerHTML = this.getString('add');
                    }
                } else {
                    button.innerHTML = this.getString('soldOut');
                    button.classList.add('button--disabled');
                }
            } else {
                if (this.selectedVariant.available) {
                    button.innerHTML = this.getString('add');
                } else {
                    button.innerHTML = this.getString('soldOut');
                    button.disabled = true;
                    button.classList.add('button--disabled');
                }
            }
        }
    }

    /**
	 * Enable AddToCart.
	 */
    protected handleInStock() {
        for (const button of (this.submitButton || [])) {
            button.disabled = false;
            button.classList.remove('button--disabled');
            button.innerHTML = this.getString('add');
        }
    }

    /**
	 * Get Language string set within the product template.
	 * @param string
	 * @returns
	 */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}
